import {useCallback} from 'react';
import {useLogger} from 'src/utils/logger';
import {Wallet} from 'src/types';
import type {SupportedCurrency} from 'src/v2/state/types';
import {getPermitMessage} from 'src/v2/components/pay/getPermitMessage';
import {getStore} from 'src/v2/state/store';
import {encodePermitFunctionData} from 'src/v2/components/pay/encodePermitFunctionData';
import {useWalletConnectionClient} from 'src/v2/state/walletConnection';
import {getAbiForAsset} from 'src/utils/getAbiForAsset';

export const useSignPermitMessage = () => {
  const walletClient = useWalletConnectionClient();
  const store = getStore();
  // Get values from store
  const activeWallet = store.useSelector(
    (state) => state.walletConnection.activeWallet,
  );

  const spenderContractAddresses = store.useSelector(
    (state) => state.spenderContractAddresses,
  );

  const validBefore = store.useSelector((state) => state.validBefore);

  const logger = useLogger();

  const signPermitMessage = useCallback(
    async ({
      asset,
      permitValue,
    }: {
      asset: SupportedCurrency;
      permitValue?: bigint;
    }): Promise<`0x${string}`> => {
      // An active wallet should be set at this point and this should not be null
      if (!activeWallet) {
        throw new Error('Active wallet not found');
      }
      const wallet = activeWallet as Wallet;

      if (!spenderContractAddresses) {
        throw new Error('Spender contract addresses not found');
      }
      const spenderContractAddress: string =
        spenderContractAddresses[`${asset.assetCode}.${asset.currencyNetwork}`];

      const tokenName: string = await walletClient.evmReadContract({
        abi: getAbiForAsset(asset),
        address: asset.contractAddress as `0x${string}`,
        functionName: 'name',
        args: [],
      });

      const nonce: bigint = await walletClient.evmReadContract({
        abi: getAbiForAsset(asset),
        address: asset.contractAddress as `0x${string}`,
        functionName: 'nonces',
        args: [wallet.address as `0x${string}`],
      });

      let tokenVersion = '2';
      try {
        tokenVersion = await walletClient.evmReadContract({
          abi: getAbiForAsset(asset),
          address: asset.contractAddress as `0x${string}`,
          functionName: 'version',
          args: [],
        });
      } catch {
        /* some tokens don’t expose version() */
      }

      const {domain, types, message, primaryType} = getPermitMessage({
        spender: spenderContractAddress,
        ownerWallet: wallet.address,
        asset,
        tokenName,
        nonce,
        tokenVersion,
        validBefore,
        permitValue,
      });

      logger.info('Prompting user to sign permit message');

      const signature = await walletClient.evmSignTypedData({
        domain,
        types,
        message,
        primaryType,
      });

      logger.info('User signed permit message', {
        signature,
      });

      // If no signature is returned, something went wrong (user cancelled, etc.), so just return null
      if (!signature) {
        throw new Error('No signature returned');
      }

      // Encode the permit function call with the user's signature
      const encodedFunctionData = encodePermitFunctionData({
        asset,
        owner: wallet.address,
        spender: spenderContractAddress,
        value: message.value,
        deadline: message.deadline,
        signature,
      });

      logger.info('Encoded permit function data', {
        encodedFunctionData,
      });

      return encodedFunctionData;
    },
    [logger, activeWallet, spenderContractAddresses, walletClient, validBefore],
  );

  return {signPermitMessage};
};
